import { Colors } from "swing-components";

import { MarkdownContent } from "~components";
import {
  AccordionAnswer,
  CheckboxAnswer,
  ChoiceAnswer,
  CloudinaryUploadAnswer,
  ContentAnswer,
  DistrictsAnswer,
  DownloadAnswer,
  EmailAnswer,
  LAContactPersonAnswer,
  LinkAnswer,
  NumberAnswer,
  OutlineAnswer,
  PhoneAnswer,
  PhoneVerifyAnswer,
  RadioAnswer,
  SignatureAnswer,
  TextAnswer,
  TextAreaAnswer,
  UploadAnswer,
  WebinarConfirmationAnswer,
  WebinarDatesAnswer,
} from "~onboarding/components";
import type { AnswerType, QuestionComputed, UnsyncedSubAttribute } from "~onboarding/utils";
import { WebinarOption } from "~utils";

import { PIIAnswer } from "../Answers/PIIAnswer/PIIAnswer";

type QuestionProps = QuestionComputed & {
  /**
   * When answering a Question, this function will pass the `value` to the
   * parent Step component.
   */
  onChange: ({ attribute, value }: UnsyncedSubAttribute) => void;
  /**
   * Disables the editability of the Question
   */
  isReadOnly?: boolean;
};

export function Question(props: QuestionProps) {
  const {
    attribute,
    label,
    description,
    value,
    answerPlacement = "BOTTOM",
    onChange,
    isReadOnly,
    answerType,
  } = props;

  /***** Handler *****/
  /**
   * Pass the value to the parent Step component only when the Question is not
   * in a readonly state.
   */
  function handleChange(value: UnsyncedSubAttribute["value"]) {
    // Do not sent updates when the Question is read-only
    if (isReadOnly) return;

    // Otherwise, pass the identifier and value to the parent Step component
    onChange({ attribute, value });
  }

  return (
    <div
      css={{
        border: "none",
        backgroundColor: Colors.white200,
        display: "flex",
        flexDirection: "column",
        gap: "var(--spacing-lg)",
        color: "black",
      }}
    >
      {/* Answer */}
      {answerPlacement === "TOP" && (
        <Answer
          answerType={answerType}
          onChange={handleChange}
          value={value}
          isReadOnly={isReadOnly}
        />
      )}

      {/* Label */}
      <div css={{ margin: 0, fontWeight: "var(--swing-font-semibold)" }}>
        <MarkdownContent body={label} testId="question-label" />
      </div>

      {/* Description */}
      {description && (
        <div css={{ margin: 0 }}>
          <MarkdownContent body={description} testId="question-description" />
        </div>
      )}

      {/* Answer */}
      {answerPlacement === "BOTTOM" && (
        <Answer
          answerType={answerType}
          onChange={handleChange}
          value={value}
          isReadOnly={isReadOnly}
        />
      )}
    </div>
  );
}

/********** Helper Component *********/
type AnswerProps = {
  answerType: AnswerType;
  onChange: (value: UnsyncedSubAttribute["value"]) => void;
  value?: string | object;
  isReadOnly?: boolean;
};

/** Renders any answer type */
function Answer(props: AnswerProps) {
  const { answerType, onChange, value, isReadOnly } = props;
  const { type } = answerType;

  switch (type) {
    /***** Render - Content Types */
    case "ACCORDION":
      return <AccordionAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly} />;
    case "CONTENT":
      return <ContentAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly} />;
    case "OUTLINE":
      return <OutlineAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly} />;

    /***** Render - Click Types */
    case "LINK":
      return <LinkAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly} />;
    case "DOWNLOAD":
      return <DownloadAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly} />;
    case "UPLOAD":
      return (
        <UploadAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly || !!value} />
      );
    case "CLOUDINARY_UPLOAD":
      return (
        <CloudinaryUploadAnswer
          {...answerType}
          onChange={onChange}
          isReadOnly={isReadOnly || !!value}
        />
      );

    /***** Render - Input Types */
    case "TEXT":
      return (
        <TextAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    case "EMAIL":
      return (
        <EmailAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    case "PII":
      return (
        <PIIAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    case "DISTRICTS":
      return <DistrictsAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly} />;
    case "TEXTAREA":
      return (
        <TextAreaAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    case "NUMBER":
      return (
        <NumberAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    case "PHONE":
      return (
        <PhoneAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    case "PHONE_VERIFY":
      return (
        <PhoneVerifyAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );

    /***** Render - List Types */
    case "RADIO":
      return (
        <RadioAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    case "CHECKBOX":
      return (
        <CheckboxAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    case "WEBINAR_DATES":
      return (
        <WebinarDatesAnswer
          {...answerType}
          onChange={onChange}
          value={value as WebinarOption}
          isReadOnly={isReadOnly}
        />
      );
    case "WEBINAR_CONFIRMATION":
      return <WebinarConfirmationAnswer {...answerType} value={value as WebinarOption} />;
    case "SIGNATURE":
      return (
        <SignatureAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly || !!value} />
      );
    case "CHOICE":
      return (
        <ChoiceAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    case "LA_CONTACT_PERSON":
      return <LAContactPersonAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly} />;
    default: {
      // Exhaustive check: if we add a new type to AnswerType, TypeScript will error here if not handled above
      void (type as never);
      return null;
    }
  }
}
